import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { useAppContext } from '../App';
import { useTranslation } from 'react-i18next';
import { Star, MapPin, Heart, ChevronRight } from 'lucide-react';
import SkeletonCard from './SkeletonCard';

const FavoritesPage = () => {
  const { user, favorites, toggleFavorite } = useAppContext();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [stadiums, setStadiums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user || favorites.length === 0) {
        setStadiums([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Firestore "in" query is limited to 10 items usually, but for this scale it's fine.
        // For production with many favorites, we might need a different approach,
        // but this is the standard way for < 10 items.
        const q = query(
          collection(db, 'stadiums'),
          where(documentId(), 'in', favorites)
        );
        
        const querySnapshot = await getDocs(q);
        const fetchedStadiums = [];
        querySnapshot.forEach((doc) => {
          fetchedStadiums.push({ id: doc.id, ...doc.data() });
        });
        setStadiums(fetchedStadiums);
      } catch (error) {
        console.error("Error loading favorites:", error);
      }
      setLoading(false);
    };

    fetchFavorites();
  }, [user, favorites]);

  if (!user) return null; // Protected by App.js logic usually

  return (
    <div className={`min-h-screen bg-neutral-100 dark:bg-neutral-900 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-neutral-800 dark:text-white mb-8">{t('favorites.title')}</h1>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : stadiums.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-neutral-800 rounded-3xl shadow-lg">
            <Heart size={48} className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
            <h3 className="text-xl font-semibold text-neutral-600 dark:text-neutral-400 mb-4">{t('favorites.noFavorites')}</h3>
            <button
              onClick={() => navigate('/stadiums')}
              className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition"
            >
              {t('favorites.browseButton')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stadiums.map((stadium) => (
              <div
                key={stadium.id} 
                className="group glass-card glass-card-hover rounded-2xl overflow-hidden text-left flex flex-col relative"
              >
                {/* Favorite Button Overlay */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(stadium.id);
                  }}
                  className="absolute top-4 right-4 z-20 p-2 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm rounded-full hover:bg-white dark:hover:bg-neutral-700 transition shadow-luxury"
                >
                  <Heart 
                    size={20} 
                    className={`transition-colors ${favorites.includes(stadium.id) ? 'fill-red-500 text-red-500' : 'text-neutral-400 dark:text-neutral-500'}`} 
                  />
                </button>

                {/* Image with h-56 to match Homepage */}
                <div className="relative h-56 overflow-hidden cursor-pointer" onClick={() => navigate(`/stadium/${stadium.id}`)}>
                  <img
                    src={stadium.photos[0]}
                    alt={stadium.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  
                  {/* Glass Rating Badge - Match Homepage style */}
                  <div className="absolute bottom-4 left-4 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl px-4 py-2 rounded-full flex items-center gap-2 shadow-luxury border border-white/20">
                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                    <span className="font-bold text-neutral-800 dark:text-white">{stadium.rating}</span>
                    <span className="text-neutral-600 dark:text-neutral-400 text-sm">({stadium.reviewCount || 0})</span>
                  </div>
                </div>
                
                {/* Content with p-6 to match Homepage */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-neutral-800 dark:text-neutral-100 mb-2 group-hover:text-primary dark:group-hover:text-primary-400 transition-colors cursor-pointer" onClick={() => navigate(`/stadium/${stadium.id}`)}>
                    {stadium.name}
                  </h3>
                  <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300 mb-4">
                    <MapPin size={16} />
                    <span>{stadium.location}</span>
                  </div>

                  <div className="mt-auto">
                    <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
                      <div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">{t('listingpage.startingFrom')}</div>
                        <div className="text-2xl font-bold text-primary dark:text-primary-400">
                          ${Math.min(...(stadium.sports || []).map(s => s.pricePerSlot) || [0])}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/stadium/${stadium.id}`)}
                        className="flex items-center gap-2 text-primary dark:text-primary-400 font-semibold hover:gap-3 transition-all"
                      >
                        <span>{t('listingpage.viewDetails')}</span>
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;